'use client';

import Image from 'next/image';
import type { CSSProperties } from 'react';

interface AdminResponsiveBannerProps {
  alt: string;
  desktopSrc: string;
  tabletSrc?: string;
  mobileSrc?: string;
  priority?: boolean;
  sizes?: string;
  className?: string;
  imageClassName?: string;
  backgroundColor?: string;
  maxWidth?: number;
  desktopAspectRatio?: number;
  tabletAspectRatio?: number;
  mobileAspectRatio?: number;
  desktopBreakpoint?: number;
  tabletBreakpoint?: number;
}

const DEFAULT_SIZES = '(max-width: 767px) 100vw, (max-width: 1199px) 100vw, 1536px';

export default function AdminResponsiveBanner({
  alt,
  desktopSrc,
  tabletSrc,
  mobileSrc,
  priority = false,
  sizes = DEFAULT_SIZES,
  className = '',
  imageClassName = '',
  backgroundColor = '#f5eee0',
  maxWidth,
  desktopAspectRatio = 2560 / 700,
  tabletAspectRatio = 1600 / 700,
  mobileAspectRatio = 1080 / 1350,
  desktopBreakpoint = 1200,
  tabletBreakpoint = 768,
}: AdminResponsiveBannerProps) {
  const fallbackTabletSrc = tabletSrc || desktopSrc;
  const fallbackMobileSrc = mobileSrc || fallbackTabletSrc;

  const shellStyle = {
    backgroundColor,
    maxWidth: maxWidth ? `${maxWidth}px` : undefined,
    '--banner-desktop-ratio': `${desktopAspectRatio}`,
    '--banner-tablet-ratio': `${tabletAspectRatio}`,
    '--banner-mobile-ratio': `${mobileAspectRatio}`,
  } as CSSProperties & Record<string, string | number | undefined>;

  return (
    <section
      className={`admin-responsive-banner ${className}`.trim()}
      style={shellStyle}
      data-fixed-height="false"
    >
      <picture>
        <source media={`(max-width: ${tabletBreakpoint - 1}px)`} srcSet={fallbackMobileSrc} />
        <source media={`(max-width: ${desktopBreakpoint - 1}px)`} srcSet={fallbackTabletSrc} />
        <Image
          src={desktopSrc}
          alt={alt}
          fill
          sizes={sizes}
          priority={priority}
          loading={priority ? 'eager' : 'lazy'}
          fetchPriority={priority ? 'high' : 'auto'}
          className={`admin-responsive-banner-image ${imageClassName}`.trim()}
          style={{ objectFit: 'cover' }}
        />
      </picture>

      <style jsx>{`
        .admin-responsive-banner {
          position: relative;
          width: 100%;
          overflow: hidden;
          margin: 0 auto;
        }

        .admin-responsive-banner {
          aspect-ratio: var(--banner-desktop-ratio);
        }

        @media (max-width: 1199px) {
          .admin-responsive-banner {
            aspect-ratio: var(--banner-tablet-ratio, var(--banner-desktop-ratio));
          }
        }

        @media (max-width: 767px) {
          .admin-responsive-banner {
            aspect-ratio: var(
              --banner-mobile-ratio,
              var(--banner-tablet-ratio, var(--banner-desktop-ratio))
            );
          }
        }

        .admin-responsive-banner :global(picture) {
          display: block;
          width: 100%;
          height: 100%;
        }
      `}</style>
    </section>
  );
}
